import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getGroupLeaveInfo, leaveChatGroup, setChatMuted } from "../../firebase/chatPreferences";

export default function GroupChatDialog({ group, onClose, onLeft }) {
  const ref = useRef(null);
  const pending = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [leaveInfo, setLeaveInfo] = useState(null);
  useEffect(() => {
    const dialog = ref.current;
    dialog.showModal();
    return () => dialog.close();
  }, []);
  const run = async (action) => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try { await action(); }
    catch (e) { setError(e.message || "Please try again."); }
    finally { pending.current = false; setBusy(false); }
  };
  const mute = () => run(async () => {
    await setChatMuted(group.type, group.spaceId, group.stationId, !group.muted);
    onClose();
  });
  const preview = () => run(async () => {
    setLeaveInfo(await getGroupLeaveInfo(group.type, group.spaceId, group.stationId));
  });
  const leave = () => run(async () => {
    await leaveChatGroup(leaveInfo.type, group.spaceId, group.stationId);
    onLeft?.();
    onClose();
  });
  return createPortal(
    <dialog ref={ref} aria-labelledby="group-chat-title"
      onCancel={(e) => { e.preventDefault(); if (!pending.current) onClose(); }}
      style={{ maxWidth: 420, border: "1px solid var(--border1)", borderRadius: 14, padding: 24, background: "var(--paper, white)", color: "inherit" }}>
      <h3 id="group-chat-title">{leaveInfo ? (leaveInfo.owner ? "Workspace owner" : `Leave ${leaveInfo.type}?`) : group.name || "Group chat"}</h3>
      {!leaveInfo ? <p>This chat belongs to the {group.type}. Muting stops its notifications and keeps your membership. To stop participating, you can review the option to leave. Your existing messages remain.</p>
        : leaveInfo.owner ? <p>You own this workspace. Transfer ownership in workspace settings before leaving. You can mute this chat now.</p>
        : <p>{leaveInfo.inherited ? `Your access to this project comes from ${leaveInfo.name}. Leaving only this project would not remove that access. Continuing will leave the workspace and all of its projects, including their chats.` : `Leaving removes your access to this ${leaveInfo.type}${leaveInfo.type === "workspace" ? " and all of its projects, including their chats" : " and its chat"}.`} Your existing messages remain. You will need another invitation to return.</p>}
      {error && <p role="alert">{error}</p>}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
        <button type="button" disabled={busy} onClick={leaveInfo ? () => setLeaveInfo(null) : onClose}>{leaveInfo ? "Back" : "Cancel"}</button>
        {(!leaveInfo || leaveInfo.owner) && <button type="button" disabled={busy} onClick={mute}>{group.muted ? "Unmute chat" : "Mute chat"}</button>}
        {!leaveInfo && <button type="button" disabled={busy} onClick={preview}>Leave…</button>}
        {leaveInfo && !leaveInfo.owner && <button type="button" disabled={busy} onClick={leave}>Leave {leaveInfo.type}</button>}
      </div>
    </dialog>, document.body,
  );
}
